import { router } from "@inertiajs/react"
import { ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react"
import { useTranslation } from "react-i18next"

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { formatCompactNumber } from "@/lib/format-compact-number"
import { cn } from "@/lib/utils"
import { brandAnalyticsChannelPath } from "@/routes"
import {
  CHANNEL_RELOAD_ONLY,
  type ChannelMetric,
  type ChannelSort,
  type ChannelVideoRow,
  type ChannelVideos,
  type ChannelWindow,
} from "@/types/channel-analytics"

/**
 * OKS-734 — Video performance table.
 *
 * One row per roster video: Thumbnail + caption (truncated), current
 * Views / Likes / Comments / Shares, and each metric's windowed Δ (signed
 * + ▲/▼ glyph — never color alone). Sortable headers drive a SERVER-side
 * sort via the Inertia partial reload (`only: CHANNEL_RELOAD_ONLY`), so
 * the sort survives refresh / back / share-URL (state lives in the URL,
 * not `localStorage` — KD-5). Stale videos ("last seen" older than 3 days)
 * carry a muted "No longer live" `Badge` with their last-known counts.
 * Mobile reflows to stacked cards.
 */
export interface ChannelVideosTableProps {
  videos: ChannelVideos
  /** Threaded through sort/pagination links so filters survive. */
  window: ChannelWindow
  metric: ChannelMetric
  className?: string
}

const SORT_COLUMNS: ChannelSort[] = ["views", "likes", "comments", "shares"]

export function ChannelVideosTable({
  videos,
  window,
  metric,
  className,
}: ChannelVideosTableProps) {
  const { t } = useTranslation()
  const isEmpty = videos.rows.length === 0

  const applySort = (column: ChannelSort) => {
    // Toggle dir when re-clicking the active column; new column starts desc.
    const dir = videos.sort === column && videos.dir === "desc" ? "asc" : "desc"
    router.get(
      brandAnalyticsChannelPath({ window, metric, sort: column, dir }),
      undefined,
      {
        only: [...CHANNEL_RELOAD_ONLY],
        preserveState: false,
        preserveScroll: true,
      },
    )
  }

  return (
    <Card
      data-testid="channel-videos-table"
      className={cn("flex flex-col gap-3 p-4 sm:p-6", className)}
    >
      <CardHeader className="p-0">
        <CardTitle className="text-foreground text-base font-semibold">
          {t("channel.videos.title")}
        </CardTitle>
      </CardHeader>

      <CardContent className="p-0">
        {isEmpty ? (
          <EmptyState />
        ) : (
          <>
            <DesktopTable
              videos={videos}
              onSort={applySort}
              className="hidden md:block"
            />
            <MobileCards rows={videos.rows} className="md:hidden" />
            <Pagination
              videos={videos}
              window={window}
              metric={metric}
              className="mt-4"
            />
          </>
        )}
      </CardContent>
    </Card>
  )
}

interface DesktopTableProps {
  videos: ChannelVideos
  onSort: (column: ChannelSort) => void
  className?: string
}

function DesktopTable({ videos, onSort, className }: DesktopTableProps) {
  const { t } = useTranslation()
  return (
    <div className={className} data-testid="channel-videos-table-desktop">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t("channel.videos.columns.video")}</TableHead>
            {SORT_COLUMNS.map((column) => (
              <SortableHead
                key={column}
                column={column}
                activeSort={videos.sort}
                activeDir={videos.dir}
                onSort={onSort}
              />
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {videos.rows.map((row) => (
            <TableRow
              key={row.tiktok_video_id}
              data-testid="channel-videos-row"
            >
              <TableCell>
                <VideoCell row={row} />
              </TableCell>
              <MetricCell value={row.views} delta={row.d_views} />
              <MetricCell value={row.likes} delta={row.d_likes} />
              <MetricCell value={row.comments} delta={row.d_comments} />
              <MetricCell value={row.shares} delta={row.d_shares} />
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}

function SortableHead({
  column,
  activeSort,
  activeDir,
  onSort,
}: {
  column: ChannelSort
  activeSort: ChannelSort
  activeDir: string
  onSort: (column: ChannelSort) => void
}) {
  const { t } = useTranslation()
  const isActive = activeSort === column
  const ariaSort = isActive
    ? activeDir === "asc"
      ? "ascending"
      : "descending"
    : "none"
  const Icon = !isActive
    ? ArrowUpDown
    : activeDir === "asc"
      ? ArrowUp
      : ArrowDown

  return (
    <TableHead className="text-right" aria-sort={ariaSort}>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => onSort(column)}
        data-testid={`channel-videos-col-${column}`}
        className="ml-auto flex h-auto items-center gap-1 px-1 font-medium"
      >
        {t(`channel.videos.columns.${column}`)}
        <Icon className="h-3.5 w-3.5" aria-hidden />
      </Button>
    </TableHead>
  )
}

function MetricCell({ value, delta }: { value: number; delta: number }) {
  return (
    <TableCell className="text-right">
      <div className="flex flex-col items-end">
        <span className="text-foreground font-medium">
          {formatCompactNumber(value)}
        </span>
        <DeltaBadge delta={delta} />
      </div>
    </TableCell>
  )
}

function DeltaBadge({ delta }: { delta: number }) {
  const { t } = useTranslation()
  if (delta === 0) {
    return (
      <span
        className="text-muted-foreground text-xs"
        aria-label={t("channel.videos.delta_flat")}
      >
        —
      </span>
    )
  }
  const up = delta > 0
  const signed = `${up ? "+" : ""}${formatCompactNumber(delta)}`
  return (
    <span
      className={cn(
        "flex items-center gap-0.5 text-xs",
        up ? "text-emerald-600 dark:text-emerald-400" : "text-destructive",
      )}
      aria-label={t(
        up ? "channel.videos.delta_up" : "channel.videos.delta_down",
        {
          value: signed,
        },
      )}
    >
      {up ? (
        <ArrowUp className="h-3 w-3" aria-hidden />
      ) : (
        <ArrowDown className="h-3 w-3" aria-hidden />
      )}
      {signed}
    </span>
  )
}

function VideoCell({ row }: { row: ChannelVideoRow }) {
  const { t } = useTranslation()
  const content = (
    <div className="flex items-center gap-3">
      <Avatar className="h-10 w-10 rounded-md">
        {row.thumbnail_url ? (
          <AvatarImage
            src={row.thumbnail_url}
            alt=""
            className="object-cover"
          />
        ) : null}
        <AvatarFallback className="rounded-md text-xs">TT</AvatarFallback>
      </Avatar>
      <div className="flex min-w-0 flex-col gap-1">
        <span className="line-clamp-2 max-w-[32ch] text-sm">
          {row.caption?.trim() ? row.caption : t("channel.videos.no_caption")}
        </span>
        {row.stale ? (
          <Badge
            variant="secondary"
            data-testid="channel-videos-stale-badge"
            className="text-muted-foreground w-fit text-xs font-normal"
          >
            {t("channel.videos.stale_badge")}
          </Badge>
        ) : null}
      </div>
    </div>
  )

  if (!row.share_url) return content
  return (
    <a
      href={row.share_url}
      target="_blank"
      rel="noopener noreferrer"
      className="focus-visible:ring-ring rounded-sm hover:underline focus-visible:ring-2 focus-visible:outline-none"
    >
      {content}
    </a>
  )
}

function MobileCards({
  rows,
  className,
}: {
  rows: ChannelVideoRow[]
  className?: string
}) {
  const { t } = useTranslation()
  return (
    <ul
      className={cn("flex flex-col gap-3", className)}
      data-testid="channel-videos-cards"
    >
      {rows.map((row) => (
        <li
          key={row.tiktok_video_id}
          className="rounded-lg border p-3"
          data-testid="channel-videos-card"
        >
          <VideoCell row={row} />
          <dl className="mt-3 grid grid-cols-2 gap-2 text-sm">
            <MobileStat
              label={t("channel.videos.columns.views")}
              value={row.views}
              delta={row.d_views}
            />
            <MobileStat
              label={t("channel.videos.columns.likes")}
              value={row.likes}
              delta={row.d_likes}
            />
            <MobileStat
              label={t("channel.videos.columns.comments")}
              value={row.comments}
              delta={row.d_comments}
            />
            <MobileStat
              label={t("channel.videos.columns.shares")}
              value={row.shares}
              delta={row.d_shares}
            />
          </dl>
        </li>
      ))}
    </ul>
  )
}

function MobileStat({
  label,
  value,
  delta,
}: {
  label: string
  value: number
  delta: number
}) {
  return (
    <div className="flex items-center justify-between gap-2">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="flex items-center gap-1 font-medium">
        {formatCompactNumber(value)}
        <DeltaBadge delta={delta} />
      </dd>
    </div>
  )
}

function Pagination({
  videos,
  window,
  metric,
  className,
}: {
  videos: ChannelVideos
  window: ChannelWindow
  metric: ChannelMetric
  className?: string
}) {
  const { t } = useTranslation()
  const { pagination, sort, dir } = videos
  // Single page → no control (keeps the common case clean).
  if (pagination.pages <= 1) return null

  const go = (page: number) => {
    router.get(
      brandAnalyticsChannelPath({ window, metric, sort, dir, page }),
      undefined,
      {
        only: [...CHANNEL_RELOAD_ONLY],
        preserveState: false,
        preserveScroll: true,
      },
    )
  }

  return (
    <nav
      className={cn("flex items-center justify-between gap-3", className)}
      aria-label={t("channel.videos.pagination.aria")}
      data-testid="channel-videos-pagination"
    >
      <span className="text-muted-foreground text-sm">
        {t("channel.videos.pagination.status", {
          page: pagination.page,
          pages: pagination.pages,
        })}
      </span>
      <div className="flex gap-2">
        <Button
          variant="outline"
          size="sm"
          disabled={pagination.prev === null}
          onClick={() => pagination.prev !== null && go(pagination.prev)}
          data-testid="channel-videos-pagination-prev"
        >
          {t("channel.videos.pagination.prev")}
        </Button>
        <Button
          variant="outline"
          size="sm"
          disabled={pagination.next === null}
          onClick={() => pagination.next !== null && go(pagination.next)}
          data-testid="channel-videos-pagination-next"
        >
          {t("channel.videos.pagination.next")}
        </Button>
      </div>
    </nav>
  )
}

function EmptyState() {
  const { t } = useTranslation()
  return (
    <div
      data-testid="channel-videos-table-empty"
      className="text-muted-foreground flex flex-col items-center gap-2 py-8 text-sm"
    >
      <p className="text-foreground font-medium">
        {t("channel.videos.empty.title")}
      </p>
      <p className="max-w-md text-center">{t("channel.videos.empty.body")}</p>
    </div>
  )
}
